import { useQuery } from '@tanstack/react-query';

import { IBox } from '../models/box';
import useImperativeRequestWrapper from '@/hooks/useImperativeRequestWrapper';
import { ExtractFnReturnType, QueryConfig } from '@/lib/react-query';

export interface IGetBoxesParams {
  thingName: string;
  pageSize?: number;
  offset?: number;
}

export interface IGetBoxesResponse {
  boxes: IBox[];
  total?: number;
}

const getBoxes = async ({
  params,
  makeRequest,
}: {
  params: IGetBoxesParams;
  makeRequest: (config: {
    path: string;
    method: string;
    body: unknown;
  }) => Promise<{ data?: IGetBoxesResponse; error?: unknown }>;
}): Promise<IGetBoxesResponse> => {
  const { data, error } = await makeRequest({
    path: '/boxes/list',
    method: 'post',
    body: {
      thingName: params.thingName,
      pageSize: params.pageSize ?? 15,
      offset: params.offset ?? 0,
    },
  });

  if (error || !data) {
    throw new Error(error as string);
  }

  const boxes =
    data.boxes || (data as unknown as { items: IBox[] }).items || [];

  return { boxes, total: boxes.length };
};

type QueryFnType = typeof getBoxes;

type UseGetBoxesOptions = {
  params: IGetBoxesParams;
  config?: QueryConfig<QueryFnType>;
  enabled?: boolean;
};

export const useGetBoxes = ({
  params,
  config,
  enabled = true,
}: UseGetBoxesOptions) => {
  const [, makeRequest] = useImperativeRequestWrapper('device');

  return useQuery<ExtractFnReturnType<QueryFnType>>({
    ...config,
    queryKey: ['boxes', 'search', params.thingName],
    queryFn: () => getBoxes({ params, makeRequest }),
    enabled: enabled && !!params.thingName,
  });
};
